import { html } from "lit-element";
import { StatefulModule } from "../../ModuleBase/ModuleMetadata";
import { BooleanField } from "../../../../elements/inspx-boolean-field";
import { PolygonDrawer } from "./polygon-drawer";
import '../../../../elements/color-picker';

export class PolygonModule extends StatefulModule {
	Activate() {
		this.canvas.defaultCursor = "crosshair";
		this.canvas.on("mouse:down", this._mouseDownHandler);
		this.canvas.on("mouse:move", this._mouseMoveHandler);

		this.drawingHistory.pause();

		const cc = this._getCanvasContainer();
		cc.tabIndex = 0;
		cc.style.outline = 'none';
		cc.addEventListener('focusout', this._focusout);

		document.addEventListener('keydown', this._keyDown);

		this.cancel();

		return this;
	}

	Deactivate() {
		this.canvas.setCursor(this.canvas.defaultCursor);
		this.canvas.off("mouse:down", this._mouseDownHandler);
		this.canvas.off("mouse:move", this._mouseMoveHandler);

		const cc = this._getCanvasContainer();
		cc.removeEventListener('focusout', this._focusout);

		document.removeEventListener('keydown', this._keyDown);

		this.cancel();

		this.drawingHistory.unpause();
	}

	_getCanvasContainer() {
		return document.querySelector('inspx-drawing-tool').shadowRoot.querySelector('.canvas-container');
	}

	render() {
		return html`
        <style>
			:host {
				padding: 5px;
				width: 100%;
				cursor: pointer;
			}

			svg {
				cursor: pointer;
				width: 100%;
				height: 100%;
			}
		</style>
        <label>${this.icon}</label>`;
	}

	constructor(container, canvas, getColor, icon, drawingHistory, type) {
		super();

		this._container = container;
		this.canvas = canvas;
		this.icon = icon;
		this.drawingHistory = drawingHistory;
		this.polygonType = type;

		this.drawer = new PolygonDrawer(canvas, drawingHistory);

		this._mouseDownHandler = (e) => {
			this.drawer.handleMouseDown(e);
		};

		this._mouseMoveHandler = (e) => {
			this.drawer.handleMouseMove(e);
		};

		this._focusout = () => {
			if (!this.drawer.editorModeOn) {
				this.cancel();
			}
		};

		this._keyDown = (e) => {
			// esc
			if (e.keyCode === 27) {
				this.cancel();
			}
		};
	}

	save() {
		if (this.drawer.editorModeOn === true) {
			const points = this.drawer.getPolygonPoints();
			this.drawer.resetPolygon();
			if (this.drawer.editingPolygon != null) {
				this.restorePolygon(points);
				this.drawer.editingPolygon = null;
			} else {
				this.generatePolygon(points);
			}
			this.fireOnDraw();
			return;
		}
		this.drawer.resetPolygon();
	}

	edit(polygon) {
		if (!polygon) return;

		this.drawer.loadShapeFromExistingPolygon(polygon);
	}

	cancel() {
		this.drawer.cancel();
	}

	generatePolygon(points) {
		const polygon = new fabric.Polygon(points, {
			stroke: "grey",
			strokeWidth: 5,
			fill: this.polygonType === "filled" ? "grey" : this.transpColor,
			opacity: 1
		});

		this.canvas.add(polygon);
		this.addMetadata(polygon, {
			stroke: polygon.stroke,
			strokeWidth: polygon.strokeWidth,
			fill: polygon.fill,
			borderStyle: "solid"
		});

		this._saveDrawingHistory(polygon);
	}

	restorePolygon(points) {
		const polygon = new fabric.Polygon(points, {
			stroke: this.drawer.editingPolygon.stroke,
			strokeWidth: this.drawer.editingPolygon.strokeWidth,
			strokeDashArray: this.drawer.editingPolygon.strokeDashArray,
			fill: this.drawer.editingPolygon.fill,
			opacity: this.drawer.editingPolygon.opacity
		});

		this.canvas.add(polygon);
		this.addMetadata(polygon, {
			stroke: polygon.stroke,
			strokeWidth: polygon.strokeWidth,
			fill: polygon.fill,
			borderStyle: this.drawer.editingPolygon.moduleState.borderStyle
		});

		this._saveDrawingHistory(polygon);
	}

	_saveDrawingHistory(polygon) {
		this.canvas.selection = true;

		this.drawingHistory.unpause();
		this.canvas.trigger("object:modified", { target: polygon });
		this.drawingHistory.pause();
	}

	RenderSettings(obj, closeFunc) {
		const currentSettings = obj.moduleState;
		let tempWidth = currentSettings.strokeWidth;
		let tempBorderColor = currentSettings.stroke;
		let tempFillColor = currentSettings.fill;
		let tempBorderStyle = currentSettings.borderStyle || "solid";

		let cbSolid = new BooleanField();
		cbSolid.label = "solid";
		cbSolid.type = "checkbox";
		cbSolid.value = tempBorderStyle == "solid";

		let cbDotted = new BooleanField();
		cbDotted.label = "dotted";
		cbDotted.type = "checkbox";
		cbDotted.value = tempBorderStyle == "dotted";

		let cbDashed = new BooleanField();
		cbDashed.label = "dashed";
		cbDashed.type = "checkbox";
		cbDashed.value = tempBorderStyle == "dashed";

		cbDashed.addEventListener('change', (e) => {
			cbSolid.value = false; cbDotted.value = false;
			if (e.detail.value) tempBorderStyle = "dashed";
			else {
				tempBorderStyle = "solid";
				cbSolid.value = true;
			}
		});
		cbSolid.addEventListener('change', (e) => {
			cbDotted.value = false; cbDashed.value = false;
			if (e.detail.value) tempBorderStyle = "solid";
			else {
				tempBorderStyle = "solid";
				cbSolid.value = true;
			}
		});
		cbDotted.addEventListener('change', (e) => {
			cbDashed.value = false; cbSolid.value = false;
			if (e.detail.value) tempBorderStyle = "dotted";
			else {
				tempBorderStyle = "solid";
				cbSolid.value = true;
			}
		});

		// border color
		let tempBorderTransp = (tempBorderColor === this.transpColor);

		let cbBorderColor = new BooleanField();
		cbBorderColor.label = "transparent ";
		cbBorderColor.type = "checkbox";
		cbBorderColor.value = tempBorderTransp;
		cbBorderColor.addEventListener('change', (e) => {
			tempBorderTransp = e.detail.value;
			if (tempBorderTransp) {
				inpBorderColor.setAttribute("disabled", "");
				cbFillColor.setAttribute("disabled", "");
			} else {
				inpBorderColor.removeAttribute("disabled");
				cbFillColor.removeAttribute("disabled");
			}
			inpBorderColor.value = tempBorderColor = this.inputTranspColor;
		});

		let inpBorderColor = document.createElement("color-picker");
		inpBorderColor.type = "color";
		inpBorderColor.id = "bColor";
		inpBorderColor.value = tempBorderTransp ? this.inputTranspColor : tempBorderColor;
		inpBorderColor.addEventListener('change', (e) => {
			tempBorderColor = e.detail;
		});

		// fill color
		let tempFillTransp = (tempFillColor === this.transpColor);

		let cbFillColor = new BooleanField();
		cbFillColor.label = "transparent ";
		cbFillColor.type = "checkbox";
		cbFillColor.value = tempFillTransp;
		cbFillColor.addEventListener('change', (e) => {
			tempFillTransp = e.detail.value;
			if (tempFillTransp) {
				inpFillColor.setAttribute("disabled", "");
				cbBorderColor.setAttribute("disabled", "");
			} else {
				inpFillColor.removeAttribute("disabled");
				cbBorderColor.removeAttribute("disabled");
			}
			inpFillColor.value = tempFillColor = this.inputTranspColor;
		});

		let inpFillColor = document.createElement("color-picker");
		inpFillColor.type = "color";
		inpFillColor.id = "fColor";
		inpFillColor.value = tempFillTransp ? this.inputTranspColor : tempFillColor;
		inpFillColor.addEventListener('change', (e) => {
			tempFillColor = e.detail;
		});

		if (tempBorderTransp) {
			inpBorderColor.setAttribute("disabled", "");
			cbFillColor.setAttribute("disabled", "");
		}
		if (tempFillTransp) {
			inpFillColor.setAttribute("disabled", "");
			cbBorderColor.setAttribute("disabled", "");
		}

		const editFunc = () => {
			this._container.editPolygon(obj);
			closeFunc();
		};
		const applyFunc = () => {
			let border = (tempBorderTransp ? this.transpColor : tempBorderColor);
			let fill = (tempFillTransp ? this.transpColor : tempFillColor);
			this.applyNewSettings(obj, tempWidth, border, fill, tempBorderStyle);
			closeFunc();
		};
		return html`
            <style>
                .title {
                    font-size: x-large;    
                    font-weight: bold;
                    margin-bottom: 20px;
                }

                .button-box {
                    display: flex;
                    margin-top: 20px;
                }

                .cPick {
                    width: 100%;
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    color: var(--secondary-color-variant1);
                }

                .color-box {
                    color: var(--secondary-color-variant1);
                }
            
                .color-box boolean-field {
                    width: auto;
                }

                input:disabled,
                boolean-field[disabled] {
                    color: rgba(84, 84, 84, .5);
                    cursor: not-allowed;
                    opacity: .5;
                }
            </style>
            <div class="title">Polygon settings</div>    
            <div> 
                <text-box type="text" label="Border width" 
                    @change="${e => (tempWidth = e.currentTarget.value)}"
                    .value="${tempWidth}"></text-box>

				<div class="color-box">
					<label>Border style</label> 
					<div class="cPick">
						${cbSolid}
						${cbDotted}
						${cbDashed}
					</div>
				</div>
                <br/>

                <div class="color-box">
					<label for="bColor">Border color</label> 
					<div class="cPick"> 
                        ${cbBorderColor}
                        ${inpBorderColor}
					</div>
				</div>
				<br/>

				<div class="color-box">
					<label for="fColor">Fill color</label> 
					<div class="cPick"> 
                        ${cbFillColor}
                        ${inpFillColor}
					</div>
				</div>
				<br/>

				<div>
					<mwc-button @click="${editFunc}" label="Edit polygon shape"></mwc-button>
				</div>
			</div>

            <div class="button-box">
                <mwc-button @click="${closeFunc}" label="Close"></mwc-button>
                <mwc-button @click="${applyFunc}" label="Apply"></mwc-button>
            </div>`;
	}

	applyNewSettings(obj, strokeWidth, stroke, fill, borderStyle) {
		const currentSettings = obj.moduleState;
		if (currentSettings.strokeWidth == strokeWidth
            && currentSettings.stroke == stroke
            && currentSettings.fill == fill
            && currentSettings.borderStyle == borderStyle) return;

		let strokeDashArray = undefined;
		if (borderStyle == 'dotted') strokeDashArray = [strokeWidth, strokeWidth];
		if (borderStyle == 'dashed') strokeDashArray = [4 * strokeWidth, strokeWidth];

		this.addMetadata(obj, { strokeWidth: Number(strokeWidth), stroke, fill, borderStyle });
		obj.set({ strokeWidth: Number(strokeWidth), stroke, fill, strokeDashArray });

		this.canvas.renderAll();
		this.canvas.trigger("object:modified", { target: obj });
	}
}

window.customElements.define("inspx-draw-polygon-module", PolygonModule);
